import React, { useState, useEffect, useRef } from 'react';
import { X, Type, Image as ImageIcon, Link as LinkIcon, Save, AlertCircle, Plus, Copy, Trash2, List } from 'lucide-react';
import { toast } from 'react-toastify';

/**
 * VisualEmailEditor - Enhanced Visual Email Editing Interface
 * 
 * PHASE 1 & 2 ENHANCEMENTS:
 * ✅ Bullet list editing support
 * ✅ Add new sections (paragraph, heading, button, list)
 * ✅ Delete/duplicate icons on hover
 * ✅ Variable management (delete, clear all)
 * ✅ Proper spacing between elements
 * ✅ Enhanced blank template support
 */
const VisualEmailEditor = ({ value, onChange, onInsertVariable }) => {
  const [html, setHtml] = useState(value || '');
  const [editingElement, setEditingElement] = useState(null);
  const [editorModal, setEditorModal] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [showAddMenu, setShowAddMenu] = useState(null);
  const iframeRef = useRef(null);

  // Update local HTML when prop changes
  useEffect(() => {
    if (value !== html) {
      setHtml(value || '');
    }
  }, [value]);

  // Refresh iframe when HTML changes
  useEffect(() => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      
      // Add click handlers and hover toolbars to editable elements
      setTimeout(() => {
        addClickHandlers(doc);
        addHoverToolbars(doc);
      }, 100);
    }
  }, [html]);

  // Add hover toolbars with delete/duplicate actions
  const addHoverToolbars = (doc) => {
    if (!doc) return;

    // Remove existing toolbars
    doc.querySelectorAll('.hover-toolbar').forEach(el => el.remove());

    const editableSelectors = [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p',
      'img',
      'a[style*="background"]',
      'ul', 'ol'
    ];

    editableSelectors.forEach(selector => {
      doc.querySelectorAll(selector).forEach((el, idx) => {
        // Skip if inside another editable element
        if (el.closest('ul, ol') && (selector === 'li')) return;

        el.style.position = 'relative';

        // Create hover toolbar
        const toolbar = doc.createElement('div');
        toolbar.className = 'hover-toolbar';
        toolbar.setAttribute('contenteditable', 'false'); // ← ADD THIS LINE
            toolbar.style.cssText = `
  position: absolute;
  top: -12px;
  right: 0;
  background: white;
  backdrop-filter: blur(8px);
  border-radius: 6px;
  padding: 4px;
  display: none;
  gap: 4px;
  z-index: 10000;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  border: 1px solid #e5e7eb;
  pointer-events: auto;
`;

        // Duplicate button
const duplicateBtn = doc.createElement('button');
duplicateBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
</svg>`;
duplicateBtn.title = 'Duplicate';
duplicateBtn.style.cssText = `
  background: white;
  border: 1px solid #d1d5db;
  color: #6366f1;
  cursor: pointer;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 4px;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  justify-content: center;
`;
duplicateBtn.onmouseover = () => {
  duplicateBtn.style.background = '#eef2ff';
  duplicateBtn.style.borderColor = '#6366f1';
  duplicateBtn.style.transform = 'scale(1.1)';
};
duplicateBtn.onmouseout = () => {
  duplicateBtn.style.background = 'white';
  duplicateBtn.style.borderColor = '#d1d5db';
  duplicateBtn.style.transform = 'scale(1)';
};
duplicateBtn.onclick = (e) => {
  e.preventDefault();
  e.stopPropagation();
  handleDuplicateElement(el);
};

// Delete button
const deleteBtn = doc.createElement('button');
deleteBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <polyline points="3 6 5 6 21 6"></polyline>
  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
</svg>`;
deleteBtn.title = 'Delete';
deleteBtn.style.cssText = `
  background: white;
  border: 1px solid #d1d5db;
  color: #ef4444;
  cursor: pointer;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 4px;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  justify-content: center;
`;
deleteBtn.onmouseover = () => {
  deleteBtn.style.background = '#fee2e2';
  deleteBtn.style.borderColor = '#ef4444';
  deleteBtn.style.transform = 'scale(1.1)';
};
deleteBtn.onmouseout = () => {
  deleteBtn.style.background = 'white';
  deleteBtn.style.borderColor = '#d1d5db';
  deleteBtn.style.transform = 'scale(1)';
};
deleteBtn.onclick = (e) => {
  e.preventDefault();
  e.stopPropagation();
  handleDeleteElement(el);
};

// Add section button
const addBtn = doc.createElement('button');
addBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <line x1="12" y1="5" x2="12" y2="19"></line>
  <line x1="5" y1="12" x2="19" y2="12"></line>
</svg>`;
addBtn.title = 'Add Section Below';
addBtn.style.cssText = `
  background: white;
  border: 1px solid #d1d5db;
  color: #10b981;
  cursor: pointer;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 4px;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  justify-content: center;
`;
addBtn.onmouseover = () => {
  addBtn.style.background = '#d1fae5';
  addBtn.style.borderColor = '#10b981';
  addBtn.style.transform = 'scale(1.1)';
};
addBtn.onmouseout = () => {
  addBtn.style.background = 'white';
  addBtn.style.borderColor = '#d1d5db';
  addBtn.style.transform = 'scale(1)';
};
addBtn.onclick = (e) => {
  e.preventDefault();
  e.stopPropagation();
  setShowAddMenu({ afterElement: el });
};

        toolbar.appendChild(duplicateBtn);
        toolbar.appendChild(deleteBtn);
        toolbar.appendChild(addBtn);

        // Show/hide toolbar on hover
        el.addEventListener('mouseenter', () => {
          toolbar.style.display = 'flex';
        });
        el.addEventListener('mouseleave', () => {
          toolbar.style.display = 'none';
        });

        // Insert toolbar into element
        el.appendChild(toolbar);
      });
    });
  };

  // Handle duplicate element
  const handleDuplicateElement = (element) => {
    const clone = element.cloneNode(true);
    
    // Remove toolbar from clone
    const toolbar = clone.querySelector('.hover-toolbar');
    if (toolbar) toolbar.remove();

    // Add spacing
    const spacer = element.ownerDocument.createElement('div');
    spacer.style.cssText = 'height: 15px;';
    
    element.parentNode.insertBefore(spacer, element.nextSibling);
    element.parentNode.insertBefore(clone, spacer.nextSibling);

    updateHtmlFromIframe();
    toast.success('Element duplicated!');
  };

  // Handle delete element
  const handleDeleteElement = (element) => {
    if (!confirm('Are you sure you want to delete this element?')) return;

    // Remove spacing after element if exists
    if (element.nextSibling && element.nextSibling.style && element.nextSibling.style.height === '15px') {
      element.nextSibling.remove();
    }

    element.remove();
    updateHtmlFromIframe();
    toast.success('Element deleted!');
  };

  // Add click handlers to editable elements in iframe
  const addClickHandlers = (doc) => {
    if (!doc) return;

    // Find all editable elements
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const paragraphs = doc.querySelectorAll('p');
    const images = doc.querySelectorAll('img');
    const buttons = doc.querySelectorAll('a[style*="background"]');
    const lists = doc.querySelectorAll('ul, ol');

    // Add hover effect and click handler to headings
    headings.forEach((el, idx) => {
      el.style.cursor = 'pointer';
      
      el.addEventListener('mouseenter', () => {
        if (!el.querySelector('.hover-toolbar')) {
          el.style.outline = '2px dashed #3B82F6';
          el.style.outlineOffset = '2px';
        }
      });
      
      el.addEventListener('mouseleave', () => {
        el.style.outline = 'none';
      });
      
      el.addEventListener('click', (e) => {
        if (e.target.closest('.hover-toolbar')) return;
        e.preventDefault();
        e.stopPropagation();
        handleEditHeading(el, idx);
      });
    });

    // Add hover effect and click handler to paragraphs
    paragraphs.forEach((el, idx) => {
      el.style.cursor = 'pointer';
      
      el.addEventListener('mouseenter', () => {
        if (!el.querySelector('.hover-toolbar')) {
          el.style.outline = '2px dashed #10B981';
          el.style.outlineOffset = '2px';
        }
      });
      
      el.addEventListener('mouseleave', () => {
        el.style.outline = 'none';
      });
      
      el.addEventListener('click', (e) => {
        if (e.target.closest('.hover-toolbar')) return;
        e.preventDefault();
        e.stopPropagation();
        handleEditParagraph(el, idx);
      });
    });

    // Add hover effect and click handler to images
    images.forEach((el, idx) => {
      el.style.cursor = 'pointer';
      
      el.addEventListener('mouseenter', () => {
        if (!el.querySelector('.hover-toolbar')) {
          el.style.outline = '2px dashed #F59E0B';
          el.style.outlineOffset = '2px';
        }
      });
      
      el.addEventListener('mouseleave', () => {
        el.style.outline = 'none';
      });
      
      el.addEventListener('click', (e) => {
        if (e.target.closest('.hover-toolbar')) return;
        e.preventDefault();
        e.stopPropagation();
        handleEditImage(el, idx);
      });
    });

    // Add hover effect and click handler to buttons
    buttons.forEach((el, idx) => {
      el.style.cursor = 'pointer';
      
      el.addEventListener('mouseenter', () => {
        if (!el.querySelector('.hover-toolbar')) {
          el.style.outline = '2px dashed #8B5CF6';
          el.style.outlineOffset = '2px';
        }
      });
      
      el.addEventListener('mouseleave', () => {
        el.style.outline = 'none';
      });
      
      el.addEventListener('click', (e) => {
        if (e.target.closest('.hover-toolbar')) return;
        e.preventDefault();
        e.stopPropagation();
        handleEditButton(el, idx);
      });
    });

    // ✅ NEW: Add hover effect and click handler to lists
    lists.forEach((el, idx) => {
      el.style.cursor = 'pointer';
      
      el.addEventListener('mouseenter', () => {
        if (!el.querySelector('.hover-toolbar')) {
          el.style.outline = '2px dashed #EC4899';
          el.style.outlineOffset = '2px';
        }
      });
      
      el.addEventListener('mouseleave', () => {
        el.style.outline = 'none';
      });
      
      el.addEventListener('click', (e) => {
        if (e.target.closest('.hover-toolbar')) return;
        e.preventDefault();
        e.stopPropagation();
        handleEditList(el, idx);
      });
    });
  };

  // Handle editing heading
  const handleEditHeading = (element, index) => {
    setEditingElement({ type: 'heading', element, index });
    const clone = element.cloneNode(true);
const toolbarEl = clone.querySelector('.hover-toolbar');
if (toolbarEl) toolbarEl.remove();

setEditingData({
  text: clone.textContent,
  color: element.style.color || '#ffffff'
});
    setEditorModal('text');
  };

  // Handle editing paragraph
  const handleEditParagraph = (element, index) => {
  setEditingElement({ type: 'paragraph', element, index });
  
  // Clone element and remove toolbar before getting content
  const clone = element.cloneNode(true);
  const toolbarEl = clone.querySelector('.hover-toolbar');
  if (toolbarEl) toolbarEl.remove();
  
  setEditingData({
    text: clone.innerHTML  // ← Now gets clean HTML without toolbar
  });
  setEditorModal('text');
};

  // Handle editing image
  const handleEditImage = (element, index) => {
    setEditingElement({ type: 'image', element, index });
    setEditingData({
      src: element.src,
      alt: element.alt || ''
    });
    setEditorModal('image');
  };

  // Handle editing button
  const handleEditButton = (element, index) => {
    setEditingElement({ type: 'button', element, index });
    const clone = element.cloneNode(true);
const toolbarEl = clone.querySelector('.hover-toolbar');
if (toolbarEl) toolbarEl.remove();

setEditingData({
  text: clone.textContent,
  href: element.href || '',
  backgroundColor: extractBackgroundColor(element.style.cssText)
});
    setEditorModal('button');
  };

  // ✅ NEW: Handle editing list
  const handleEditList = (element, index) => {
    const items = Array.from(element.querySelectorAll('li')).map(li => li.innerHTML);
    setEditingElement({ type: 'list', element, index, listType: element.tagName.toLowerCase() });
    setEditingData({
      items: items,
      listType: element.tagName.toLowerCase()
    });
    setEditorModal('list');
  };

  // Extract background color from style string
  const extractBackgroundColor = (styleText) => {
    const match = styleText.match(/background-color:\s*(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3}|rgb\([^)]+\))/);
    return match ? match[1] : '#0066CC';
  };

  // Save text changes
  const handleSaveText = () => {
    if (!editingElement) return;

    const { type, element } = editingElement;

    if (type === 'heading') {
      element.textContent = editingData.text;
      if (editingData.color) {
        element.style.color = editingData.color;
      }
    } else if (type === 'paragraph') {
      element.innerHTML = editingData.text;
    }

    updateHtmlFromIframe();
    closeModal();
    toast.success('Text updated!');
  };

  // Save image changes
  const handleSaveImage = () => {
    if (!editingElement) return;

    const { element } = editingElement;
    element.src = editingData.src;
    element.alt = editingData.alt;

    updateHtmlFromIframe();
    closeModal();
    toast.success('Image updated!');
  };

  // Handle image upload (images only, max 2MB)
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - images only
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are supported (JPG, PNG, GIF, etc.)');
      e.target.value = '';
      return;
    }

    // Validate file size (max 2MB for base64 email embedding)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB for email compatibility');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setEditingData(prev => ({ ...prev, src: event.target.result }));
      toast.success('Image loaded!');
    };
    reader.readAsDataURL(file);
  };

  // Save button changes
  const handleSaveButton = () => {
    if (!editingElement) return;

    const { element } = editingElement;
    element.textContent = editingData.text;
    element.href = editingData.href;
    
    const styleText = element.style.cssText;
    const newStyle = styleText.replace(
      /background-color:\s*[^;]+/,
      `background-color: ${editingData.backgroundColor}`
    );
    element.style.cssText = newStyle;

    updateHtmlFromIframe();
    closeModal();
    toast.success('Button updated!');
  };

  // ✅ NEW: Save list changes
  const handleSaveList = () => {
    if (!editingElement) return;

    const { element } = editingElement;
    
    // Clear existing items
    element.innerHTML = '';
    
    // Add updated items
    editingData.items.forEach(itemHtml => {
      const li = element.ownerDocument.createElement('li');
      li.innerHTML = itemHtml;
      li.style.cssText = 'margin: 8px 0; font-size: 16px; color: #333333;';
      element.appendChild(li);
    });

    updateHtmlFromIframe();
    closeModal();
    toast.success('List updated!');
  };

  // ✅ NEW: Add list item
  const handleAddListItem = () => {
    setEditingData(prev => ({
      ...prev,
      items: [...prev.items, 'New item']
    }));
  };

  // ✅ NEW: Remove list item
  const handleRemoveListItem = (index) => {
    setEditingData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // ✅ NEW: Update list item
  const handleUpdateListItem = (index, value) => {
    setEditingData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? value : item)
    }));
  };

  // Update HTML from iframe content
  const updateHtmlFromIframe = () => {
    if (!iframeRef.current) return;

    const doc = iframeRef.current.contentDocument;
    
    // Remove hover toolbars before extracting HTML
    doc.querySelectorAll('.hover-toolbar').forEach(el => el.remove());
    
    const newHtml = doc.documentElement.outerHTML;
    setHtml(newHtml);
    
    if (onChange) {
      onChange(newHtml);
    }
  };

  // Close modal
  const closeModal = () => {
    setEditorModal(null);
    setEditingElement(null);
    setEditingData({});
    setShowAddMenu(null);
  };

  // ✅ NEW: Insert variable in text editor
  const handleInsertVariableInEditor = () => {
    const variables = [
      { label: 'Customer Name', value: '{{name}}' },
      { label: 'Email', value: '{{email}}' },
      { label: 'Phone', value: '{{phone}}' },
      { label: 'Business Unit', value: '{{businessUnit}}' },
      { label: 'Loyalty Points', value: '{{loyaltyPoints}}' },
      { label: 'Unsubscribe Link', value: '{{unsubscribeLink}}' }
    ];

    const choice = prompt(
      'Choose variable:\n' +
      variables.map((v, i) => `${i + 1}. ${v.label} - ${v.value}`).join('\n')
    );

    const index = parseInt(choice) - 1;
    if (index >= 0 && index < variables.length) {
      const variable = variables[index].value;
      setEditingData(prev => ({
        ...prev,
        text: prev.text + ' ' + variable
      }));
    }
  };

  // ✅ NEW: Clear all variables from text
  const handleClearAllVariables = () => {
    if (!confirm('Remove all variables from this text?')) return;
    
    const cleaned = editingData.text.replace(/\{\{[^}]+\}\}/g, '');
    setEditingData(prev => ({
      ...prev,
      text: cleaned
    }));
    toast.success('Variables removed!');
  };

  // ✅ NEW: Detect and show variables as chips
  const detectVariables = (text) => {
    const regex = /\{\{([^}]+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push(match[0]);
    }
    return [...new Set(matches)];
  };

  // ✅ NEW: Remove specific variable
  const handleRemoveVariable = (variable) => {
    const text = editingData.text.replace(new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '');
    setEditingData(prev => ({
      ...prev,
      text: text
    }));
  };

  // ✅ NEW: Add new section
  const handleAddSection = (type) => {
    const doc = iframeRef.current.contentDocument;

    // Find the content area td - exclude header/footer tds that have background styles
    const allPaddedTds = doc.querySelectorAll('td[style*="padding: 40px 30px"]');
    let contentSection = null;

    for (const td of allPaddedTds) {
      const style = td.getAttribute('style') || '';
      if (!style.includes('background')) {
        contentSection = td;
        break;
      }
    }

    // Fallback: pick the second td (content area) or first available
    if (!contentSection && allPaddedTds.length >= 2) {
      contentSection = allPaddedTds[1];
    } else if (!contentSection && allPaddedTds.length >= 1) {
      contentSection = allPaddedTds[0];
    }

    if (!contentSection) {
      toast.error('Cannot find content section');
      setShowAddMenu(null);
      return;
    }

    // Find proper insertion point - walk afterElement up to a direct child of contentSection
    let insertAfter = showAddMenu?.afterElement;

    if (insertAfter) {
      while (insertAfter && insertAfter.parentNode !== contentSection) {
        if (!insertAfter.parentNode || insertAfter.parentNode === doc) {
          insertAfter = null;
          break;
        }
        insertAfter = insertAfter.parentNode;
      }
    }

    if (!insertAfter) {
      insertAfter = contentSection.lastChild;
    }

    let newElement;

    switch(type) {
      case 'paragraph':
        newElement = doc.createElement('p');
        newElement.style.cssText = 'margin: 15px 0; font-size: 16px; color: #333333;';
        newElement.textContent = 'Click to edit this paragraph...';
        break;

      case 'heading':
        newElement = doc.createElement('h2');
        newElement.style.cssText = 'margin: 20px 0 10px 0; font-size: 24px; color: #333333; font-weight: 600;';
        newElement.textContent = 'Click to edit heading';
        break;

      case 'button': {
        const table = doc.createElement('table');
        table.setAttribute('width', '100%');
        table.setAttribute('cellpadding', '0');
        table.setAttribute('cellspacing', '0');
        table.setAttribute('border', '0');
        table.style.cssText = 'margin: 25px 0;';

        const tr = doc.createElement('tr');
        const td = doc.createElement('td');
        td.setAttribute('align', 'center');

        const link = doc.createElement('a');
        link.href = 'https://wa.me/2349055444495';
        link.style.cssText = 'display: inline-block; background-color: #0066CC; color: #ffffff; text-decoration: none; padding: 15px 40px; border-radius: 5px; font-weight: 600; font-size: 16px;';
        link.textContent = 'Click to Edit Button';

        td.appendChild(link);
        tr.appendChild(td);
        table.appendChild(tr);
        newElement = table;
        break;
      }

      case 'image':
        newElement = doc.createElement('img');
        newElement.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="600" height="200"><rect width="600" height="200" fill="#e5e7eb" rx="8"/><text x="300" y="95" text-anchor="middle" font-family="Arial" font-size="18" fill="#6b7280">Click to upload your image</text><text x="300" y="120" text-anchor="middle" font-family="Arial" font-size="13" fill="#9ca3af">JPG, PNG, GIF - Max 2MB</text></svg>');
        newElement.alt = 'Click to upload image';
        newElement.style.cssText = 'max-width: 100%; height: auto; display: block; margin: 20px auto; border-radius: 8px; cursor: pointer;';
        break;

      case 'list':
        newElement = doc.createElement('ul');
        newElement.style.cssText = 'margin: 20px 0; padding-left: 20px;';

        for (let i = 0; i < 3; i++) {
          const li = doc.createElement('li');
          li.style.cssText = 'margin: 8px 0; font-size: 16px; color: #333333;';
          li.textContent = `List item ${i + 1}`;
          newElement.appendChild(li);
        }
        break;

      default:
        setShowAddMenu(null);
        return;
    }

    // Add spacing and insert the new element
    const spacer = doc.createElement('div');
    spacer.style.cssText = 'height: 15px;';

    try {
      if (insertAfter && insertAfter.nextSibling) {
        contentSection.insertBefore(spacer, insertAfter.nextSibling);
        contentSection.insertBefore(newElement, spacer.nextSibling);
      } else {
        contentSection.appendChild(spacer);
        contentSection.appendChild(newElement);
      }
    } catch (err) {
      // Fallback: append to end of content section
      contentSection.appendChild(spacer);
      contentSection.appendChild(newElement);
    }

    updateHtmlFromIframe();
    setShowAddMenu(null);
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} added!`);
  };

  // Render Text Editor Modal
  const renderTextEditorModal = () => {
    const isHeading = editingElement?.type === 'heading';
    const variables = detectVariables(editingData.text || '');

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b flex items-center justify-between bg-blue-600">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Type size={20} />
              Edit {isHeading ? 'Heading' : 'Text'}
            </h3>
            <button onClick={closeModal} className="text-white hover:text-gray-200">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isHeading ? 'Heading Text' : 'Paragraph Content'}
              </label>
              <textarea
                value={editingData.text || ''}
                onChange={(e) => setEditingData(prev => ({ ...prev, text: e.target.value }))}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter text here..."
              />
            </div>

            {/* ✅ NEW: Variable Management */}
            {variables.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-purple-700">Variables in text:</label>
                  <button
                    onClick={handleClearAllVariables}
                    className="text-xs text-red-600 hover:text-red-800 font-medium"
                  >
                    Clear All
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {variables.map((variable, idx) => (
                    <div
                      key={idx}
                      className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2"
                    >
                      <code>{variable}</code>
                      <button
                        onClick={() => handleRemoveVariable(variable)}
                        className="text-purple-500 hover:text-purple-700"
                        title="Remove this variable"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isHeading && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Color
                </label>
                <input
                  type="color"
                  value={editingData.color || '#ffffff'}
                  onChange={(e) => setEditingData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-20 h-10 border border-gray-300 rounded cursor-pointer"
                />
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleInsertVariableInEditor}
                className="px-4 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition text-sm font-medium"
              >
                📋 Insert Variable
              </button>
            </div>

            <p className="text-xs text-gray-500">
              💡 Tip: Use variables like {'{{name}}'}, {'{{email}}'}, etc. for personalization
            </p>
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
            <button
              onClick={closeModal}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveText}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition flex items-center gap-2"
            >
              <Save size={16} />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render Image Editor Modal
  const renderImageEditorModal = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b flex items-center justify-between bg-orange-600">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <ImageIcon size={20} />
              Edit Image
            </h3>
            <button onClick={closeModal} className="text-white hover:text-gray-200">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image URL
              </label>
              <input
                type="text"
                value={editingData.src || ''}
                onChange={(e) => setEditingData(prev => ({ ...prev, src: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or Upload Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              <p className="text-xs text-gray-500 mt-1">
                Images only (JPG, PNG, GIF). Max 2MB. Will be embedded in email.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alt Text
              </label>
              <input
                type="text"
                value={editingData.alt || ''}
                onChange={(e) => setEditingData(prev => ({ ...prev, alt: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Image description"
              />
            </div>

            {editingData.src && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview
                </label>
                <img
                  src={editingData.src}
                  alt={editingData.alt}
                  className="max-w-full h-auto border border-gray-300 rounded"
                />
              </div>
            )}
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
            <button
              onClick={closeModal}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveImage}
              className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition flex items-center gap-2"
            >
              <Save size={16} />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render Button Editor Modal
  const renderButtonEditorModal = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b flex items-center justify-between bg-purple-600">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <LinkIcon size={20} />
              Edit Button
            </h3>
            <button onClick={closeModal} className="text-white hover:text-gray-200">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Button Text
              </label>
              <input
                type="text"
                value={editingData.text || ''}
                onChange={(e) => setEditingData(prev => ({ ...prev, text: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Click Here"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Button Link (URL)
              </label>
              <input
                type="text"
                value={editingData.href || ''}
                onChange={(e) => setEditingData(prev => ({ ...prev, href: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="https://example.com or https://wa.me/2349055444495"
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Tip: Use WhatsApp link format: https://wa.me/2349055444495?text=Your+Message
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Button Background Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={editingData.backgroundColor || '#0066CC'}
                  onChange={(e) => setEditingData(prev => ({ ...prev, backgroundColor: e.target.value }))}
                  className="w-20 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <span className="text-sm text-gray-600">{editingData.backgroundColor || '#0066CC'}</span>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Button Preview
              </label>
              <div className="flex justify-center">
                <a
                  href="#"
                  style={{
                    display: 'inline-block',
                    backgroundColor: editingData.backgroundColor || '#0066CC',
                    color: '#ffffff',
                    textDecoration: 'none',
                    padding: '15px 40px',
                    borderRadius: '5px',
                    fontWeight: '600',
                    fontSize: '16px'
                  }}
                  onClick={(e) => e.preventDefault()}
                >
                  {editingData.text || 'Button Text'}
                </a>
              </div>
            </div>
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
            <button
              onClick={closeModal}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveButton}
              className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition flex items-center gap-2"
            >
              <Save size={16} />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ✅ NEW: Render List Editor Modal
  const renderListEditorModal = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b flex items-center justify-between bg-pink-600">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <List size={20} />
              Edit List
            </h3>
            <button onClick={closeModal} className="text-white hover:text-gray-200">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">
                List Items
              </label>
              <button
                onClick={handleAddListItem}
                className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition text-sm font-medium flex items-center gap-1"
              >
                <Plus size={14} />
                Add Item
              </button>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {editingData.items && editingData.items.map((item, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleUpdateListItem(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder={`Item ${index + 1}`}
                  />
                  <button
                    onClick={() => handleRemoveListItem(index)}
                    className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition"
                    title="Remove item"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

            {editingData.items && editingData.items.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>No items in list. Click "Add Item" to start.</p>
              </div>
            )}
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
            <button
              onClick={closeModal}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveList}
              className="px-6 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 transition flex items-center gap-2"
            >
              <Save size={16} />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ✅ NEW: Render Add Section Menu
  const renderAddSectionMenu = () => {
    if (!showAddMenu) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="p-4 border-b bg-gradient-to-r from-green-500 to-emerald-500">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Plus size={20} />
              Add New Section
            </h3>
          </div>

          <div className="p-4 grid grid-cols-2 gap-3">
            <button
              onClick={() => handleAddSection('paragraph')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition text-left"
            >
              <div className="text-2xl mb-2">📝</div>
              <div className="font-semibold text-gray-900">Paragraph</div>
              <div className="text-xs text-gray-500">Add text content</div>
            </button>

            <button
              onClick={() => handleAddSection('heading')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition text-left"
            >
              <div className="text-2xl mb-2">📰</div>
              <div className="font-semibold text-gray-900">Heading</div>
              <div className="text-xs text-gray-500">Add section title</div>
            </button>

            <button
              onClick={() => handleAddSection('image')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition text-left"
            >
              <div className="text-2xl mb-2">🖼️</div>
              <div className="font-semibold text-gray-900">Image</div>
              <div className="text-xs text-gray-500">Add an image</div>
            </button>

            <button
              onClick={() => handleAddSection('list')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-pink-500 hover:bg-pink-50 transition text-left"
            >
              <div className="text-2xl mb-2">📋</div>
              <div className="font-semibold text-gray-900">List</div>
              <div className="text-xs text-gray-500">Add bullet points</div>
            </button>

            <button
              onClick={() => handleAddSection('button')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition text-left"
            >
              <div className="text-2xl mb-2">🔘</div>
              <div className="font-semibold text-gray-900">Button</div>
              <div className="text-xs text-gray-500">Add CTA button</div>
            </button>
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end">
            <button
              onClick={() => setShowAddMenu(null)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="visual-email-editor">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-blue-800 font-medium">
            👁️ Visual Mode - Click any element to edit
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Hover over elements to see <strong>📋 Duplicate</strong>, <strong>🗑️ Delete</strong>, and <strong>➕ Add</strong> actions
          </p>
          <p className="text-xs text-blue-600 mt-1">
            <span className="inline-block w-3 h-3 bg-blue-500 rounded mr-1"></span>Headings •
            <span className="inline-block w-3 h-3 bg-green-500 rounded mx-1"></span>Paragraphs •
            <span className="inline-block w-3 h-3 bg-orange-500 rounded mx-1"></span>Images •
            <span className="inline-block w-3 h-3 bg-purple-500 rounded mx-1"></span>Buttons •
            <span className="inline-block w-3 h-3 bg-pink-500 rounded mx-1"></span>Lists
          </p>
        </div>
      </div>

      {/* Live Preview */}
      <div className="border border-gray-300 rounded-lg overflow-hidden bg-gray-50">
        <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
          <h4 className="text-sm font-semibold text-gray-700">📧 Live Preview - Hover & Click to Edit</h4>
        </div>
        <div className="p-6 overflow-auto" style={{ minHeight: '600px' }}>
          <div className="bg-white shadow-lg rounded-lg max-w-2xl mx-auto">
            <iframe
              ref={iframeRef}
              className="w-full border-0"
              style={{ minHeight: '500px' }}
              title="Email Preview"
              sandbox="allow-same-origin allow-scripts"
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      {editorModal === 'text' && renderTextEditorModal()}
      {editorModal === 'image' && renderImageEditorModal()}
      {editorModal === 'button' && renderButtonEditorModal()}
      {editorModal === 'list' && renderListEditorModal()}
      {showAddMenu && renderAddSectionMenu()}
    </div>
  );
};

export default VisualEmailEditor;